import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  BarChart,
  TrendingUp,
  Plus,
  PieChart,
  BookOpen,
  Bot,
  ChevronRight,
  Zap,
  Lock,
  Newspaper,
  Sparkles,
  Compass,
  Trophy,
  LayoutDashboard,
  Menu,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Home,
  Award
} from 'lucide-react';
import Logo from './Logo';
import { useTranslation } from 'react-i18next';
import { useSidebar } from '../context/SidebarContext';

interface DemoSidebarProps {
  onAddCex?: () => void;
  onAddDex?: () => void;
}

const DemoSidebar: React.FC<DemoSidebarProps> = ({ onAddCex, onAddDex }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen } = useSidebar();

  const currentPath = window.location.pathname.replace(/\/$/, '') || '/demo/dashboard';

  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileOpen(false);
  };

  const handleLockClick = () => {
    setIsMobileOpen(false);
    navigate('/register');
  };

  return (
    <>
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-[60] w-10 h-10 bg-black border border-neutral-800 rounded-xl flex items-center justify-center text-neutral-400 hover:text-white transition-colors"
      >
        <Menu size={20} />
      </button>

      {isMobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 z-[9993] bg-black/60 backdrop-blur-sm"
            onClick={() => setIsMobileOpen(false)}
          />
          <div data-lenis-prevent className="md:hidden fixed inset-y-0 left-0 z-[9995] w-72 bg-black border-r border-neutral-900 overflow-y-auto">
            <div className="flex flex-col min-h-full p-6">
              <div className="flex items-center justify-between mb-6">
                <Logo size="lg" centered={false} />
                <button
                  onClick={() => setIsMobileOpen(false)}
                  className="text-neutral-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex gap-2 mb-4">
                <button
                  onClick={() => { onAddCex?.(); setIsMobileOpen(false); }}
                  className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-red-600 hover:bg-red-700 shadow-red-900/20 text-sm cursor-pointer"
                >
                  <Plus size={16} />
                  CEX
                </button>
                <button
                  onClick={() => { onAddDex?.(); setIsMobileOpen(false); }}
                  className="flex-1 py-2 text-white rounded-xl font-bold flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] bg-purple-600 hover:bg-purple-700 shadow-purple-900/20 text-sm cursor-pointer"
                >
                  <Plus size={16} />
                  DEX
                </button>
              </div>

              <nav className="space-y-0.5">
                <div className="pt-2 pb-1 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <LayoutDashboard size={10} className="text-neutral-400" /> Main Features
                </div>
                <NavItem icon={<BarChart size={16} />} label="Dashboard Demo" onClick={() => handleNavigation('/demo/dashboard')} active={currentPath === '/demo/dashboard'} isCollapsed={false} />
                <NavItem icon={<TrendingUp size={16} />} label="Trades Demo" onClick={() => handleNavigation('/demo/trades')} active={currentPath === '/demo/trades'} isCollapsed={false} />
                <NavItem icon={<PieChart size={16} />} label="Reports" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />
                <NavItem icon={<BookOpen size={16} />} label="Notebook" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />
                <NavItem icon={<Bot size={16} />} label="AI Assistant" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />

                <div className="pt-2 pb-0.5 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <Zap size={10} className="text-purple-400" /> Premium Features
                </div>
                <NavItem icon={<Newspaper size={16} />} label="Crypto News" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />
                <NavItem icon={<Sparkles size={16} />} label="Dev Picks" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />
                <NavItem icon={<Compass size={16} />} label="Top 50 Trending" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />
                <NavItem icon={<Trophy size={16} />} label="Top Traders" onClick={handleLockClick} active={false} locked={true} isCollapsed={false} />

                <div className="py-2">
                  <div className="h-px bg-neutral-900 w-full mb-2" />
                  <NavItem icon={<Home size={16} />} label="Back to Landing" onClick={() => handleNavigation('/')} isCollapsed={false} />
                </div>
              </nav>

              <div className="mt-auto pt-4 space-y-2">
                <div className="flex items-center gap-2 px-2 py-1.5 rounded-xl border border-red-900/40 bg-red-950/20">
                  <Zap size={12} className="text-red-500" />
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-widest text-red-500">
                      Guest Demo
                    </p>
                  </div>
                </div>

                <div
                  onClick={() => handleNavigation('/register')}
                  className="bg-gradient-to-br from-neutral-900 to-neutral-950 p-3 rounded-3xl border border-neutral-800 relative overflow-hidden group cursor-pointer hover:border-neutral-700 transition-all"
                >
                  <div className="absolute -top-12 -right-12 w-24 h-24 bg-red-600 rounded-full blur-[40px] opacity-20 group-hover:opacity-40 transition-opacity" />
                  <div className="relative z-10">
                    <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 shadow-lg bg-red-600 shadow-red-900/40">
                      <Zap size={16} className="text-white" />
                    </div>
                    <h3 className="text-xs font-black text-white mb-1 uppercase tracking-tight">
                      Get FREE 7 Days PRO
                    </h3>
                    <p className="text-[9px] text-neutral-500 leading-tight mb-2">
                      Create an account to start your free 7-day Pro trial and track live performance!
                    </p>
                    <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest">
                      Create Account <ChevronRight size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <div
        data-lenis-prevent
        className={`hidden md:flex flex-col transition-all duration-300 ${isCollapsed ? 'w-16' : 'w-72'} bg-black border-r border-neutral-900 h-screen sticky top-0 relative overflow-x-hidden`}
      >
        <div className={isCollapsed ? 'px-2' : 'p-6'}>
          <div className="flex flex-col items-center pt-4 pb-2">
            <Logo size={isCollapsed ? 'sm' : 'lg'} centered />
          </div>

          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="mx-auto flex items-center justify-center w-7 h-7 rounded-full bg-neutral-900 border border-neutral-700 hover:border-neutral-500 text-neutral-400 hover:text-white transition-all shadow-md mb-3"
          >
            {isCollapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
          </button>

          {isCollapsed ? (
            <div className="flex flex-col gap-1.5 mb-4">
              <button
                onClick={onAddCex}
                title="Connect CEX API"
                className="w-10 h-10 mx-auto flex items-center justify-center bg-red-600 hover:bg-red-700 rounded-xl transition-all shadow-lg active:scale-[0.98] cursor-pointer"
              >
                <Plus size={18} className="text-white" />
              </button>
              <button
                onClick={onAddDex}
                title="Connect Web3 Wallet"
                className="w-10 h-10 mx-auto flex items-center justify-center bg-purple-600 hover:bg-purple-700 rounded-xl transition-all shadow-lg active:scale-[0.98] cursor-pointer"
              >
                <Plus size={18} className="text-white" />
              </button>
            </div>
          ) : (
            <div className="flex gap-2 mb-4">
              <button
                onClick={onAddCex}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] cursor-pointer"
              >
                <Plus size={14} />
                CEX
              </button>
              <button
                onClick={onAddDex}
                className="flex-1 py-2 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 transition-all shadow-lg active:scale-[0.98] cursor-pointer"
              >
                <Plus size={14} />
                DEX
              </button>
            </div>
          )}
        </div>

        <div className="flex flex-col flex-1 overflow-y-auto">
          <div className={isCollapsed ? 'px-2' : 'px-6'}>
            <nav className="space-y-0.5">
              {!isCollapsed && (
                <div className="pt-2 pb-1 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <LayoutDashboard size={10} className="text-neutral-400" /> Main Features
                </div>
              )}
              <NavItem icon={<BarChart size={16} />} label="Dashboard Demo" onClick={() => navigate('/demo/dashboard')} active={currentPath === '/demo/dashboard'} isCollapsed={isCollapsed} />
              <NavItem icon={<TrendingUp size={16} />} label="Trades Demo" onClick={() => navigate('/demo/trades')} active={currentPath === '/demo/trades'} isCollapsed={isCollapsed} />
              <NavItem icon={<PieChart size={16} />} label="Reports" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />
              <NavItem icon={<BookOpen size={16} />} label="Notebook" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />
              <NavItem icon={<Bot size={16} />} label="AI Assistant" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />

              {!isCollapsed && (
                <div className="pt-2 pb-0.5 text-[10px] font-black text-neutral-500 uppercase tracking-widest px-3 flex items-center gap-1.5">
                  <Zap size={10} className="text-purple-400" /> Premium Features
                </div>
              )}
              <NavItem icon={<Newspaper size={16} />} label="Crypto News" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />
              <NavItem icon={<Sparkles size={16} />} label="Dev Picks" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />
              <NavItem icon={<Compass size={16} />} label="Top 50 Trending" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />
              <NavItem icon={<Trophy size={16} />} label="Top Traders" onClick={handleLockClick} active={false} locked={true} isCollapsed={isCollapsed} />

              <div className="py-2">
                <div className="h-px bg-neutral-900 w-full mb-2" />
                <NavItem icon={<Home size={16} />} label="Back to Landing" onClick={() => navigate('/')} isCollapsed={isCollapsed} />
              </div>
            </nav>
          </div>

          {!isCollapsed && (
            <div className="mt-auto p-3 space-y-2">
              <div className="flex items-center gap-2 px-2 py-1.5 rounded-xl border border-red-900/40 bg-red-950/20">
                <Zap size={12} className="text-red-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-black uppercase tracking-widest text-red-500">
                    Guest Demo
                  </p>
                </div>
              </div>

              <div
                onClick={() => navigate('/register')}
                className="bg-gradient-to-br from-neutral-900 to-neutral-950 p-3 rounded-3xl border border-neutral-800 relative overflow-hidden group cursor-pointer hover:border-neutral-700 transition-all"
              >
                <div className="absolute -top-12 -right-12 w-24 h-24 bg-red-600 rounded-full blur-[40px] opacity-20 group-hover:opacity-40 transition-opacity" />
                <div className="relative z-10">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center mb-2 shadow-lg bg-red-600 shadow-red-900/40">
                    <Zap size={16} className="text-white" />
                  </div>
                  <h3 className="text-xs font-black text-white mb-1 uppercase tracking-tight">
                    Get FREE 7 Days PRO
                  </h3>
                  <p className="text-[9px] text-neutral-500 leading-tight mb-2">
                    Create an account to start your free 7-day Pro trial and track live performance!
                  </p>
                  <div className="flex items-center gap-1 text-[10px] font-black text-red-500 uppercase tracking-widest">
                    Create Account <ChevronRight size={10} />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: () => void;
  locked?: boolean;
  isCollapsed?: boolean;
  badge?: string;
}

const NavItem: React.FC<NavItemProps> = ({ icon, label, active = false, onClick, locked = false, isCollapsed = false, badge }) => (
  <button
    onClick={onClick}
    title={isCollapsed ? label : (locked ? 'Register/login to access this feature' : undefined)}
    className={isCollapsed
      ? `w-10 h-10 mx-auto flex items-center justify-center rounded-xl transition-all ${locked
        ? 'text-neutral-700 hover:text-red-500/80 cursor-pointer'
        : active
          ? 'bg-red-700/10 text-red-600 cursor-pointer'
          : 'text-neutral-500 hover:text-white hover:bg-neutral-900 cursor-pointer'
      }`
      : `w-full p-2 rounded-xl flex items-center gap-3 font-bold transition-all group ${locked
        ? 'text-neutral-700 hover:text-red-500/80 cursor-pointer'
        : active
          ? 'bg-red-700/10 text-red-600 border-l-4 border-red-600 rounded-l-none pl-4 cursor-pointer'
          : 'text-neutral-500 hover:text-white hover:bg-neutral-900 cursor-pointer'
      }`
    }
  >
    <span className={`${locked ? 'text-neutral-700 group-hover:text-red-500 transition-colors' : active ? 'text-red-600' : 'text-neutral-500 group-hover:text-red-500 transition-colors'}`}>
      {icon}
    </span>
    {!isCollapsed && <span className="text-xs tracking-tight flex-1 text-left">{label}</span>}
    {!isCollapsed && badge && (
      <span className="px-1.5 py-0.5 bg-red-600/15 border border-red-500/20 text-red-500 rounded text-[8px] font-black uppercase tracking-wider">
        {badge}
      </span>
    )}
    {!isCollapsed && locked && <Lock size={12} className="text-neutral-700 group-hover:text-red-500 flex-shrink-0 transition-colors" />}
  </button>
);

export default DemoSidebar;
